import React, {FC, memo} from "react";
import {Tooltip} from "antd";
import {InfoCircleOutlined} from "@ant-design/icons";
import css from "./PanelCardCourseData.less";
import PanelCard from "../PanelCard/PanelCard";
import {GetBookingByIdWithCourseLessonBookingsQuery, GetCourseByIdQuery, TablesEnum} from "../../generated/graphql";
import {marginS, marginXs} from "../../styles/layout";
import CourseLessonTable from "../CourseLessonTable/CourseLessonTable";
import i18n from "../../services/i18n";
import Button from "../Button/Button";
import {AnnotationTextField} from "./AnnotationTextField";
import LessonTablePassive from "../../containers/courses/LessonBookings/LessonTablePassive";
import {useHolidays} from "../../containers/courses/LessonBookings/helper";
import {useLocalStorageOptions} from "../../helpers/columnsLocalStorageHandler";

type RenderCourseLessonsProps = {
    course: GetCourseByIdQuery["courseById"];
    booking?: GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];
    gapAfter?: boolean;
};

const RenderCourseLessons: FC<RenderCourseLessonsProps> = ({course, booking, gapAfter}) => {
    const tablesEnum: TablesEnum = TablesEnum.LessonBookingDetails;
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName: tablesEnum,
        defaultOptions: {},
        totalResults: 0,
    });
    const firstCourseLesson = course.lessons[0].startDateTime;
    const lastCourseLesson = course.lessons[course.lessons.length - 1].endDateTime;
    const {holidays, loadingHolidays, getHolidayInfo} = useHolidays(firstCourseLesson, lastCourseLesson);

    const renderCourseLessons = () => {
        return (
            <PanelCard
                columns={1}
                gapAfter={gapAfter}
                title={`${course.lessons.length} Kurseinheiten`}
                style={{
                    overflow: "hidden",
                    height: `calc(100% - ${marginXs})`,
                }}
            >
                {course.id && (
                    <div style={{marginTop: marginS}}>
                        <CourseLessonTable courseId={course.id} totalPrice={booking?.grossPrice} defaultPageSize={course.lessons.length} />
                    </div>
                )}
            </PanelCard>
        );
    };

    const renderCourseLessonsLessonBookings = () => {
        if (!booking) {
            return null;
        }

        return (
            <PanelCard
                columns={1}
                gapAfter={gapAfter}
                title={`${course.lessons.length} Kurseinheiten`}
                style={{
                    overflow: "hidden",
                    height: `calc(100% - ${marginXs})`,
                }}
            >
                {course.id && (
                    <div style={{marginTop: marginS}}>
                        <LessonTablePassive booking={booking} getHolidayInfo={getHolidayInfo} options={options} setOptions={setOptions} extraSmall={true} />
                    </div>
                )}
            </PanelCard>
        );
    };

    const renderCourseAnnotation = () => {
        return (
            <PanelCard
                columns={1}
                gapAfter={gapAfter}
                title={i18n.containers.courses.CourseCreate.courseInfo.label()}
                style={{
                    overflow: "hidden",
                    height: `calc(100% - ${marginXs})`,
                }}
                extra={
                    <Tooltip title={i18n.containers.courses.CourseCreate.courseInfo.description()} placement="left">
                        <Button type="link" style={{margin: 0}}>
                            <InfoCircleOutlined />
                        </Button>
                    </Tooltip>
                }
            >
                <div
                    style={{
                        paddingTop: `calc(${marginXs} + 2px)`,
                    }}
                >
                    <AnnotationTextField courseId={course.id} courseLessonCount={course.lessons.length} placeholder={i18n.containers.courses.CourseCreate.courseInfo.placeholder()} />
                </div>
            </PanelCard>
        );
    };

    return (
        <div className={css.twoColumns}>
            {booking?.courseLessonBookings?.length ? renderCourseLessonsLessonBookings() : renderCourseLessons()}
            {renderCourseAnnotation()}
        </div>
    );
};

export default memo(RenderCourseLessons);
